import { ApiRef, createApiRef } from '@backstage/core-plugin-api';
import {
  BillingInformation,
  BillingInformationValidationResponse,
  OrderSummary,
  OrderTenant,
  OwnerConfirmationResult,
  Order,
  OrderDataProduct,
  Tenant,
  OrderStatus,
  BusinessUnit,
} from '@mercedes-benz/backstage-plugin-adp-common';

export interface AdpApi {
  /**
   * Get example users.
   *
   * @public
   */
  getTenants(platform?: 'DATABRICKS' | 'AWS'): Promise<Tenant[]>;
  getBusinessUnits(): Promise<BusinessUnit[]>;
  orderTenant(tenantData: OrderTenant): Promise<Order>;
  orderDataProduct(dataProductData: OrderDataProduct): Promise<Order>;
  getOrderStatus(orderId: string): Promise<OrderStatus>;
  getOrder(orderId: string): Promise<Order>;
  validateBillingInformation(
    billingInfo: BillingInformation,
  ): Promise<BillingInformationValidationResponse>;
  /**
   * Accept or reject an owner-confirmation gated order via the signed link
   * sent to the resolved approver(s). Proxies to the adp-backend plugin,
   * which enforces a Backstage-session membership check before forwarding
   * to the FastAPI confirmation endpoint (defense in depth: the token is
   * validated server-side too).
   *
   * @public
   */
  resolveOwnerConfirmation(
    orderId: string,
    action: 'accept' | 'reject',
    token: string,
  ): Promise<OwnerConfirmationResult>;
  /**
   * Request a new confirmation email for an owner-confirmation gated order.
   * Approver-gated the same way as {@link AdpApi.resolveOwnerConfirmation}.
   *
   * @public
   */
  resendOwnerConfirmation(
    orderId: string,
    token: string,
  ): Promise<OwnerConfirmationResult>;
  /**
   * Get the sanitized name/kind summary of an order, used by the
   * owner-confirmation approval page to show the approver what they are
   * being asked to confirm. Approver-gated the same way as
   * {@link AdpApi.resolveOwnerConfirmation}.
   *
   * @public
   */
  getOrderSummary(orderId: string): Promise<OrderSummary>;
}

/**
 * ApiRef for the AdpApi.
 *
 * @public
 */
export const adpApiRef: ApiRef<AdpApi> = createApiRef<AdpApi>({
  id: 'plugin.adp.api',
});
